import { useWindowSize } from "@react-hookz/web";
import { cn } from "@babybjorn/ui";
import {
	useEffect,
	useMemo,
	useState,
	type CSSProperties,
	type FC,
	type PropsWithChildren,
	type ReactNode,
} from "react";
import {
	useIsDesktop,
	useIsMinHeight,
} from "../../../../../hooks/use-is-screensize";
import Button from "../../../Button";
import Icon from "../../../Icon";
import * as s from "./NavButton.module.scss";

type NavButtonProps = PropsWithChildren<{
	title: string;
	image: ReactNode;
	href?: string;
	onClick?: () => void;
	openDrawerIndex: number | null | undefined;
	index: number;
	previousOpenDrawerIndex?: number | null;
	headerHeight?: number;
}>;

const NavButton: FC<NavButtonProps> = ({
	image,
	title,
	href,
	onClick,
	openDrawerIndex,
	index,
	children,
	previousOpenDrawerIndex,
	headerHeight,
}) => {
	const mobileNavProps = useMemo(
		() => ({
			className: s.mobileNavItem,
		}),
		[],
	);
	const mobileNavElement = useMemo(() => {
		const children = (
			<>
				<div className={s.imageContainer}>{image}</div>
				<label className={s.label}>{title}</label>
				{!href && <Icon icon="arrow-right" />}
			</>
		);

		return href ? (
			<a {...mobileNavProps} href={href}>
				{children}
			</a>
		) : (
			<button {...mobileNavProps} onClick={onClick}>
				{children}
			</button>
		);
	}, [href, mobileNavProps, onClick, image, title]);

	const isHigh = useIsMinHeight(900);
	const isDesktop = useIsDesktop();
	const { height: innerHeight } = useWindowSize();

	const [navElement, setNavElement] = useState<ReactNode>(mobileNavElement);
	const [drawerStyle, setDrawerStyle] = useState<CSSProperties>({});
	useEffect(() => {
		if (isDesktop) {
			setNavElement(null);
			setDrawerStyle({
				top: headerHeight + "px",
				minHeight:
					index === openDrawerIndex
						? isHigh
							? innerHeight - (headerHeight || 0) + `px`
							: `calc(80vh - ${headerHeight}px)`
						: "0%",
			});
		} else {
			setNavElement(mobileNavElement);
			setDrawerStyle({});
		}
	}, [
		headerHeight,
		index,
		innerHeight,
		isDesktop,
		isHigh,
		mobileNavElement,
		openDrawerIndex,
	]);

	return (
		<li className={s.navButton}>
			<Button
				label={title}
				variant="noBg"
				buttonType="navigation"
				icon={href ? undefined : "arrow-down"}
				className={cn(s.desktopNavItem, {
					[s.open]: index === openDrawerIndex,
				})}
				{...(href ? {} : { onClick: onClick })}
				href={href && href}
			/>
			{navElement}
			<div
				className={cn(s.drawer, {
					[s.isOpen]: index === openDrawerIndex,
					[s.shouldTransition]: previousOpenDrawerIndex === null,
					[s.previouslyOpen]: previousOpenDrawerIndex === index,
				})}
				style={drawerStyle}
			>
				{children}
			</div>
		</li>
	);
};
export default NavButton;
